// =============================================================
// inapp.ts — 메신저 인앱 브라우저 감지 & 외부 브라우저 탈출
//
//  왜 필요한가:
//    카카오톡/라인/인스타 등의 인앱 웹뷰는
//      · window.ethereum 이 없고(주입 지갑 불가),
//      · wc:/metamask: 같은 외부 앱 스킴 호출도 차단/무시한다.
//    → Reown 모달은 뜨지만 지갑 앱으로 넘어가지 못해 '멈춘 것처럼' 보인다.
//    즉 인앱 브라우저에서는 연결 자체가 구조적으로 불가능하므로,
//    외부 브라우저(크롬/사파리)로 내보내는 것 외에 방법이 없다.
//
//  전략:
//    · 랜딩(콘텐츠 열람)은 인앱에서도 그대로 허용 → 마케팅 유입 유지.
//    · '지갑 연결' 을 시도하는 순간에만 탈출 오버레이를 띄운다.
//    · 상단에 얇은 배너로 미리 알려 준다(선택적으로 미리 탈출 가능).
//
//  플랫폼별 탈출 수단:
//    · Android : intent:// 스킴으로 크롬을 직접 지정 (가장 확실)
//    · Kakao   : kakaotalk://web/openExternal?url=... (iOS/Android 공통)
//    · Line    : URL 에 ?openExternalBrowser=1 을 붙이면 라인이 외부로 연다
//    · iOS 기타 : 범용 탈출 스킴이 없다 → URL 복사 + 수동 안내가 최선
// =============================================================

export type InAppKind =
  | 'kakao'
  | 'line'
  | 'instagram'
  | 'facebook'
  | 'naver'
  | 'band'
  | 'daum'
  | 'wechat'
  | 'other'
  | null;

const UA = (): string => navigator.userAgent ?? '';

export function isIOS(): boolean {
  return /iPhone|iPad|iPod/i.test(UA());
}
export function isAndroid(): boolean {
  return /Android/i.test(UA());
}

/** 메신저/소셜 인앱 웹뷰 종류. 일반 브라우저면 null. */
export function detectInApp(): InAppKind {
  const ua = UA();
  if (/KAKAOTALK/i.test(ua)) return 'kakao';
  if (/\bLine\//i.test(ua) || /LIFF/i.test(ua)) return 'line';
  if (/Instagram/i.test(ua)) return 'instagram';
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return 'facebook';
  if (/NAVER\(inapp/i.test(ua) || /NAVER/i.test(ua)) return 'naver';
  if (/BAND\//i.test(ua)) return 'band';
  if (/DaumApps/i.test(ua)) return 'daum';
  if (/MicroMessenger/i.test(ua)) return 'wechat';

  // 휴리스틱: 안드로이드 WebView 표식(wv) — 크롬 커스텀탭/일반 크롬은 wv 가 없다.
  if (isAndroid() && /\bwv\b/i.test(ua)) return 'other';

  return null;
}

/** 인앱 웹뷰인가 (= 지갑 연결이 구조적으로 불가한 환경인가). */
export function isInAppBrowser(): boolean {
  return detectInApp() !== null;
}

/** 사람이 읽을 수 있는 앱 이름. */
export function inAppName(kind: InAppKind = detectInApp()): string {
  switch (kind) {
    case 'kakao':
      return '카카오톡';
    case 'line':
      return 'LINE';
    case 'instagram':
      return 'Instagram';
    case 'facebook':
      return 'Facebook';
    case 'naver':
      return '네이버';
    case 'band':
      return '밴드';
    case 'daum':
      return '다음';
    case 'wechat':
      return 'WeChat';
    default:
      return '인앱 브라우저';
  }
}

/** 현재 페이지의 절대 URL (탈출 대상). */
function currentUrl(): string {
  return window.location.href;
}

/**
 * 외부 브라우저로 탈출을 시도한다.
 * @returns true = 탈출 시도함(페이지 이동 발생 가능) / false = 자동 탈출 불가(수동 안내 필요)
 */
export function escapeToExternalBrowser(): boolean {
  const kind = detectInApp();
  const url = currentUrl();

  // ── 1) 카카오톡: 전용 외부열기 스킴 (iOS/Android 공통, 가장 안정적) ──
  if (kind === 'kakao') {
    window.location.href = `kakaotalk://web/openExternal?url=${encodeURIComponent(url)}`;
    return true;
  }

  // ── 2) 라인: 쿼리 파라미터로 외부 브라우저 강제 ──
  if (kind === 'line') {
    const u = new URL(url);
    u.searchParams.set('openExternalBrowser', '1');
    window.location.href = u.toString();
    return true;
  }

  // ── 3) 안드로이드 일반: intent:// 로 크롬 직접 지정 ──
  //    scheme=https 로 원 URL 을 복원시키고, 크롬이 없으면 브라우저 선택창이 뜬다.
  if (isAndroid()) {
    const noScheme = url.replace(/^https?:\/\//, '');
    window.location.href =
      `intent://${noScheme}#Intent;scheme=https;` +
      `package=com.android.chrome;` +
      `S.browser_fallback_url=${encodeURIComponent(url)};end`;
    return true;
  }

  // ── 4) iOS 기타(인스타/페북 등): 범용 탈출 스킴이 없다 → 수동 안내 ──
  return false;
}

/** URL 을 클립보드에 복사 (iOS 수동 탈출용). */
export async function copyCurrentUrl(): Promise<boolean> {
  const url = currentUrl();
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    // 클립보드 API 불가 → 임시 input 으로 폴백
    try {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, url.length);
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

/** iOS 에서 '수동으로 외부 브라우저 여는 법' 안내 문구. */
export function manualEscapeHint(kind: InAppKind = detectInApp()): string {
  if (isIOS()) {
    switch (kind) {
      case 'kakao':
        return '우측 하단 [⋯] → [다른 브라우저로 열기] 를 눌러 주세요.';
      case 'instagram':
      case 'facebook':
        return '우측 상단 [⋯] → [브라우저에서 열기] 를 눌러 주세요.';
      default:
        return '메뉴에서 [Safari로 열기] 또는 [브라우저에서 열기] 를 눌러 주세요.';
    }
  }
  return '메뉴에서 [다른 브라우저로 열기] 를 눌러 주세요.';
}